import { useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  getSubItemsForIcon,
  getParentRouteForIcon,
  CustomerDetailsSideBar,
} from '@/constants/sidebar';
import { Routes } from '@/constants/routes';
import type { SideBarTooltipItem } from './SideBarHoverTooltip';

export const useSubItemsNavigation = (items: SideBarTooltipItem[], activeIndex?: number) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isCustomerDetail = location.pathname.includes(
    `/${Routes.Customers}/${Routes.Customer}/`,
  );

  const handleSubItemClick = useCallback(
    (subItemPath: string, mainItemIcon: string) => {
      const parentPath = getParentRouteForIcon(mainItemIcon);
      // Query mitnehmen: `?id=` traegt den Kunden, `&case=` seit 17.08.2026
      // den Einstiegs-Fall. Ohne sie landet der Klick auf einer Kundenseite
      // ohne Kunden.
      const fullPath = parentPath
        ? `/${Routes.Home}/${Routes.Customers}/${Routes.Customer}/${parentPath}/${subItemPath}${location.search}`
        : '';
      if (fullPath) {
        navigate(fullPath);
      }
    },
    [navigate, location.search],
  );

  const subItems = useMemo(() => {
    if (activeIndex !== undefined && items[activeIndex]) {
      const item = items[activeIndex];
      if (item.label) return [];
      return getSubItemsForIcon(item.icon ?? '');
    }
    return [];
  }, [activeIndex, items]);

  const handleLabelClick = useCallback(
    (itemPath: string, mainItemIcon: string) => {
      const activeItem = activeIndex !== undefined ? items[activeIndex] : undefined;
      const currentSubItems =
        activeItem && !activeItem.label
          ? getSubItemsForIcon(activeItem.icon ?? '')
          : [];
      const isCustomerDetailItem = CustomerDetailsSideBar.some(
        (sidebarItem) => sidebarItem.icon === mainItemIcon,
      );

      if (currentSubItems.length > 0) {
        const parentPath = getParentRouteForIcon(mainItemIcon);
        const fullPath = parentPath
          ? `/${Routes.Home}/${Routes.Customers}/${Routes.Customer}/${parentPath}/${currentSubItems[0].path}${location.search}`
          : '';
        if (fullPath) navigate(fullPath);
      } else if (isCustomerDetail && isCustomerDetailItem) {
        navigate(
          `/${Routes.Home}/${Routes.Customers}/${Routes.Customer}/${itemPath}${location.search}`,
        );
      } else {
        navigate(`/${Routes.Home}/${itemPath}`);
      }
    },
    [navigate, activeIndex, items, isCustomerDetail, location.search],
  );

  return {
    subItems,
    handleSubItemClick,
    handleLabelClick,
  };
};
